import { create } from 'zustand';
import {
  TaxComputationCombinedVO,
  TaxComputationResult,
  TaxDepreciationAllowanceScheduleVO,
  TaxWithholdingScheduleVO,
  TaxAdditionsNonCurrentAssetsVO,
  TaxObligationUnderFinanceLeasesVO,
  TaxPpeDetailsVO,
  ProfitsTaxAVO,
  ProfitsTaxBVO,
  ProfitsTaxCVO,
  TaxComputationType,
} from '@/services/auditService/interface/tax-com';
import { auditTaxComService } from '@/services/auditService/auditTaxComService';

function normalizeYoa(yearOfAssessment?: string | null): string {
  return (yearOfAssessment ?? '').trim();
}

/**
 * Tax Store State Interface
 * 用于管理税务计算相关数据。
 *
 * 后端 `queryTaxCombined` 接口一次性返回主结果 + P1.1/P1.1a/P1.2/P1.21/P1.3 子附表：
 * - 当公司 principalActivities == 0（Inactive）时，子附表全部为 null；
 * - 当子附表查询结果不含任何数据时，对应字段为 null。
 *
 * 因此 store 不再区分多套 loading/error 状态，只维护一个统一的 loading/error。
 */
interface TaxStoreState {
  // 主要税务计算结果
  taxComputationResult: TaxComputationResult | null;

  // P1.1 - Depreciation Allowance Schedule (折旧津贴表)
  p11Data: TaxDepreciationAllowanceScheduleVO | null;

  // P1.1a - Withholding Tax Schedule (Excel P1.1 (2))
  p11aData: TaxWithholdingScheduleVO | null;

  // P1.2 - Additions to Non-Current Assets (新增非流动资产)
  p12Data: TaxAdditionsNonCurrentAssetsVO | null;

  // P1.21 - Obligation Under Finance Leases (融资租赁义务)
  p121Data: TaxObligationUnderFinanceLeasesVO | null;

  // P1.3 - PPE Details (固定资产详情)
  p13Data: TaxPpeDetailsVO | null;

  // 合并接口共享的 loading / error 状态
  loading: boolean;
  error: string | null;

  // Company and Annual context
  companyId: number | null;
  annualId: number | null;
  /** Current YOA slice; empty = legacy/SINGLE */
  yearOfAssessment: string;

  // 初始化状态标记
  isInitialized: boolean;
  isInitializing: boolean;

  // Methods
  initialize: (companyId: number, annualId: number, yearOfAssessment?: string) => Promise<void>;

  // Get individual data interface methods
  getTaxComputationResult: () => TaxComputationResult | null;
  getTaxType: () => TaxComputationType | null;
  getProfitsTaxA: () => ProfitsTaxAVO | null;
  getProfitsTaxB: () => ProfitsTaxBVO | null;
  getProfitsTaxC: () => ProfitsTaxCVO | null;
  getP11Data: () => TaxDepreciationAllowanceScheduleVO | null;
  getP11aData: () => TaxWithholdingScheduleVO | null;
  getP12Data: () => TaxAdditionsNonCurrentAssetsVO | null;
  getP121Data: () => TaxObligationUnderFinanceLeasesVO | null;
  getP13Data: () => TaxPpeDetailsVO | null;

  /**
   * 重新拉取合并接口，刷新所有 5 块数据。
   * 由于后端已合并为单接口，原 refreshTaxComputationResult / refreshP11Data 等
   * 调用全部聚合到这一个方法上。
   */
  refresh: () => Promise<void>;

  /**
   * 无感覆盖主表结果（如 stampDuty 保存后按 P1.1 4% 联动 Commercial building）。
   */
  applyTaxComputationResult: (result: TaxComputationResult | null) => void;

  /**
   * 无感覆盖 P1.1（与主表 Commercial building 联动时对齐 annualAllowance / BF）。
   */
  applyP11Data: (data: TaxDepreciationAllowanceScheduleVO | null) => void;

  // Clear all data
  clear: () => void;
}

/**
 * Tax Store - 税务计算数据管理
 */
export const useTaxStore = create<TaxStoreState>((set, get) => {
  /**
   * 内部辅助：根据 combined VO 将所有 5 块数据写入 store。
   */
  const applyCombined = (data: TaxComputationCombinedVO | null | undefined) => {
    set({
      taxComputationResult: data?.taxComputationResult || null,
      p11Data: data?.p11Data || null,
      p11aData: data?.p11aData || null,
      p12Data: data?.p12Data || null,
      p121Data: data?.p121Data || null,
      p13Data: data?.p13Data || null,
    });
  };

  /**
   * 内部辅助：拉取一次合并接口并写入 store。
   */
  const fetchCombined = async (companyId: number, annualId: number, yearOfAssessment: string) => {
    const cid = Number(companyId);
    const aid = Number(annualId);
    // Guard NaN/0 so gateway never receives companyId=NaN (MethodArgumentTypeMismatchException)
    if (!Number.isFinite(cid) || cid <= 0 || !Number.isFinite(aid) || aid <= 0) {
      console.error('Failed to load combined tax computation: invalid ids', {companyId, annualId});
      set({ loading: false, error: 'Invalid companyId or annualId' });
      return;
    }
    set({ loading: true, error: null });
    try {
      const data = await auditTaxComService.queryTaxCombined(cid, aid, yearOfAssessment || undefined);
      applyCombined(data);
    } catch (error: any) {
      console.error('Failed to load combined tax computation:', error);
      set({ error: error?.message || 'Failed to load tax computation' });
    } finally {
      set({ loading: false });
    }
  };

  return {
    // Initial state
    taxComputationResult: null,
    p11Data: null,
    p11aData: null,
    p12Data: null,
    p121Data: null,
    p13Data: null,

    loading: false,
    error: null,

    companyId: null,
    annualId: null,
    yearOfAssessment: '',
    isInitialized: false,
    isInitializing: false,

    /**
     * 初始化方法 - 调用合并接口 queryTaxCombined。
     *
     * principalActivities/子附表为空 等业务规则全部由后端处理，前端无需再做判断。
     */
    initialize: async (companyId: number, annualId: number, yearOfAssessment?: string) => {
      const state = get();
      // 子组件（TaxSubP*）常不传 YOA；省略时沿用父级（TaxDetailsTab）已设定的评估年度
      const yoa = yearOfAssessment !== undefined
        ? normalizeYoa(yearOfAssessment)
        : normalizeYoa(state.yearOfAssessment);

      // 已经针对相同 (companyId, annualId, yoa) 初始化过则跳过
      if (state.isInitialized &&
          state.companyId === companyId &&
          state.annualId === annualId &&
          state.yearOfAssessment === yoa) {
        return;
      }

      // 已经在初始化过程中则跳过，避免重复请求
      if (state.isInitializing) {
        return;
      }

      set({ companyId, annualId, yearOfAssessment: yoa, isInitializing: true, isInitialized: false });

      await fetchCombined(companyId, annualId, yoa);

      set({ isInitialized: true, isInitializing: false });
    },

    /**
     * 获取税务计算结果
     */
    getTaxComputationResult: () => get().taxComputationResult,

    /**
     * 获取税务类型
     */
    getTaxType: () => get().taxComputationResult?.type || null,

    /**
     * 获取 Profits Tax A (P1a) - Offshore profit or Inactive loss
     */
    getProfitsTaxA: () => get().taxComputationResult?.profitsTaxA || null,

    /**
     * 获取 Profits Tax B (P1b) - Simple Loss with Donations
     */
    getProfitsTaxB: () => get().taxComputationResult?.profitsTaxB || null,

    /**
     * 获取 Profits Tax C (P1c) - Complete Tax Calculation
     */
    getProfitsTaxC: () => get().taxComputationResult?.profitsTaxC || null,

    /**
     * 获取 P1.1 - Depreciation Allowance Schedule
     */
    getP11Data: () => get().p11Data,

    /**
     * 获取 P1.1a - Withholding Tax Schedule
     */
    getP11aData: () => get().p11aData,

    /**
     * 获取 P1.2 - Additions to Non-Current Assets
     */
    getP12Data: () => get().p12Data,

    /**
     * 获取 P1.21 - Obligation Under Finance Leases
     */
    getP121Data: () => get().p121Data,

    /**
     * 获取 P1.3 - PPE Details
     */
    getP13Data: () => get().p13Data,

    /**
     * 重新拉取合并接口，刷新所有 5 块数据。
     */
    refresh: async () => {
      const { companyId, annualId, yearOfAssessment } = get();
      if (!companyId || !annualId) {
        console.error('Cannot refresh: companyId or annualId is not set');
        return;
      }
      await fetchCombined(companyId, annualId, yearOfAssessment || '');
    },

    applyTaxComputationResult: (result) => {
      set({ taxComputationResult: result });
    },

    applyP11Data: (data) => {
      set({ p11Data: data });
    },

    /**
     * 清除所有数据
     */
    clear: () => {
      set({
        taxComputationResult: null,
        p11Data: null,
        p11aData: null,
        p12Data: null,
        p121Data: null,
        p13Data: null,

        loading: false,
        error: null,

        companyId: null,
        annualId: null,
        yearOfAssessment: '',
        isInitialized: false,
        isInitializing: false,
      });
    },
  };
});
